/**
 * Opportunities — `opportunities.html` ported, plus the Pipeline board as its
 * second view.
 *
 * `opportunities.html` and `pipeline.html` were two prototype pages and
 * `docs/API.md` §6 gives them separate screen keys, but they show the same
 * deals through two lenses — a filterable table and a drag-and-drop board —
 * and every internal link between them (this screen's old "Board view"
 * button, the row menu's "Open on board", the board's old List toggle) was
 * proof the split didn't earn its own nav item. `OpportunitiesPage` is now the
 * shell: it owns the List/Board toggle (synced to `?view=`) and delegates to
 * `OpportunitiesListView` below or `PipelineBoardView`
 * (`../pipeline/PipelinePage.jsx`). `/pipeline` redirects here — see
 * `routes/AppRoutes.jsx`. The `pipeline` screen permission still gates the
 * toggle itself, so this doesn't collapse the two as permissions, only as UI.
 *
 * The prototype's five tabs (Open/Closing this month/Stalled/Won/Lost) are
 * real `GET /opportunities` filters here — `is_closed`, `is_won`,
 * `close_date_from`/`to`, `stale_days_gte` — not a client-side re-filter of
 * one big list. Its "Lost opportunity analysis" and "Competitor win/loss"
 * panels both read the same `GET /analytics/lost-opportunities` call, added
 * alongside this screen; see `salescrmapi/docs/modules/opportunities.model.md`.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../app/queryClient.js';
import { useAuth } from '../../hooks/useAuth.js';
import { useDebounce } from '../../hooks/useDebounce.js';
import { useCursorPages } from '../../hooks/useCursorPages.js';
import {
  Badge, SearchInput, ChipSelect, MockBadge, ErrorState, Avatar,
} from '../../components/common/index.jsx';
import { DataTable } from '../../components/common/DataTable.jsx';
import { Spinner } from '../../components/common/Loading.jsx';
import { isMock } from '../../api/mockFallback.js';
import { money, moneyShort, date, number } from '../../utils/formatters.js';
import {
  useOpportunities, useOpportunityOptions, useCreateOpportunity, useUpdateOpportunity,
  useDeleteOpportunity, useChangeStage, useLostOpportunities, syncOpportunityRelated,
} from './useOpportunities.js';
import { toRow, forecastTone, closingThisMonthRange } from './opportunityShape.js';
import {
  OpportunityFormModal, DeleteOpportunityModal, StageChangeModal, NotBuiltModal,
} from './OpportunityModals.jsx';
import PipelineBoardView from '../pipeline/PipelinePage.jsx';

/** The shell: shared breadcrumb/title, the List/Board toggle, then whichever view is active. */
export default function OpportunitiesPage() {
  const { canOpen } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const canBoard = canOpen('pipeline');
  const view = canBoard && searchParams.get('view') === 'board' ? 'board' : 'list';

  const setView = (next) => setSearchParams(
    (prev) => {
      const params = new URLSearchParams(prev);
      if (next === 'list') params.delete('view'); else params.set('view', next);
      return params;
    },
    { replace: true },
  );

  return (
    <>
      <div className="page-head">
        <div>
          <div className="crumb"><a href="#sell">Sell</a><i className="bi bi-chevron-right" style={{ fontSize: 9 }} aria-hidden="true" /><span>Opportunities</span></div>
          <h1 className="page-title">Opportunities</h1>
        </div>
        {canBoard && (
          <div className="ms-auto seg">
            <button type="button" className={view === 'list' ? 'active' : ''} title="List" onClick={() => setView('list')}>
              <i className="bi bi-list-ul" aria-hidden="true" />
            </button>
            <button type="button" className={view === 'board' ? 'active' : ''} title="Board" onClick={() => setView('board')}>
              <i className="bi bi-kanban" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
      {view === 'board' ? <PipelineBoardView /> : <OpportunitiesListView />}
    </>
  );
}

const TABS = [
  { key: 'open', label: 'Open' },
  { key: 'closing', label: 'Closing this month' },
  { key: 'stalled', label: 'Stalled' },
  { key: 'won', label: 'Won' },
  { key: 'lost', label: 'Lost' },
];

function tabParams(tab) {
  if (tab === 'closing') return { is_closed: 'false', ...closingThisMonthRange() };
  if (tab === 'stalled') return { is_closed: 'false', stale_days_gte: 30 };
  if (tab === 'won') return { is_closed: 'true', is_won: 'true' };
  if (tab === 'lost') return { is_closed: 'true', is_won: 'false' };
  return { is_closed: 'false' };
}

const NOT_BUILT = {
  export: {
    title: 'Export is not available',
    why: '§15 (bulk/import/export) specifies an async export job with its own file and notification. None of that is built, so a real export here would silently do nothing.',
    endpoint: 'POST /exports',
    section: 'docs/API.md §15, not implemented',
  },
};

function OpportunitiesListView() {
  const { can, hasScope } = useAuth();
  const navigate = useNavigate();
  /** `own` scope means RLS already limits every result to this user — an
   * Owner filter could only ever return what's already showing or nothing,
   * so it's disabled rather than offered as a choice that can't do anything. */
  const canFilterByOwner = hasScope('opportunities', 'team');

  const [tab, setTab] = useState('open');
  const [search, setSearch] = useState('');
  const [pipelineId, setPipelineId] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [stageId, setStageId] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [stageChange, setStageChange] = useState(null);
  const [notBuilt, setNotBuilt] = useState(null);

  const debounced = useDebounce(search);
  const pages = useCursorPages(20);
  const options = useOpportunityOptions();
  const defaultPipelineId = options.pipelines.find((p) => p.isDefault)?.value ?? options.pipelines[0]?.value;

  const params = useMemo(() => ({
    ...pages.params,
    ...tabParams(tab),
    q: debounced || undefined,
    pipeline_id: pipelineId || undefined,
    owner_id: ownerId || undefined,
    stage_id: stageId || undefined,
  }), [pages.params, tab, debounced, pipelineId, ownerId, stageId]);

  const { data, isLoading, isFetching, error, refetch } = useOpportunities(params);
  const rows = useMemo(() => (data?.data ?? []).map(toRow), [data]);
  const agg = data?.aggregates;

  const { data: lostData } = useLostOpportunities({});

  const queryClient = useQueryClient();
  const createMutation = useCreateOpportunity();
  const updateMutation = useUpdateOpportunity();
  const deleteMutation = useDeleteOpportunity();
  const stageMutation = useChangeStage();

  /**
   * `useOpportunities` keeps the previous page's rows visible during a
   * background refetch (`placeholderData` in the hook) so paging Next/Previous
   * doesn't flicker. Switching tabs is a different query key entirely — Open's
   * rows sitting there while "Won" is loading reads as stale data, not a smooth
   * transition — so a tab click shows the table's own loading skeleton instead,
   * cleared the moment that tab's fetch settles.
   */
  const [switchingTab, setSwitchingTab] = useState(false);
  useEffect(() => { if (!isFetching) setSwitchingTab(false); }, [isFetching]);

  const onFilter = (setter) => (value) => { setter(value); pages.reset(); };
  const onTab = (t) => { setTab(t); pages.reset(); setSwitchingTab(true); };

  /** The open funnel only — for the stage-track mini progress bar. */
  const stagesFor = (row) => options.stages
    .filter((s) => s.pipelineId === row.pipeline?.id && !s.isClosed)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  /** Every stage in the deal's pipeline, closed ones included — a deal has to
   * be movable to Closed Won/Lost from here, not just between open stages. */
  const allStagesFor = (row) => options.stages
    .filter((s) => s.pipelineId === row.pipeline?.id)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const columns = [
    {
      key: 'name',
      header: 'Opportunity',
      render: (o) => (
        <div>
          <a
            href={`#${o.id}`} className="strong d-block text-truncate"
            onClick={(e) => { e.preventDefault(); setEditing(o); setFormOpen(true); }}
          >
            {o.name}
          </a>
          <div className="t-2xs ink-4">{o.pipeline?.name}</div>
        </div>
      ),
    },
    {
      key: 'account', header: 'Account', width: 160,
      render: (o) => (o.account
        ? <a href={`/accounts/${o.account.id}`} onClick={(e) => { e.preventDefault(); navigate(`/accounts/${o.account.id}`); }}>{o.account.name}</a>
        : <span className="text-muted">—</span>),
    },
    {
      key: 'stage', header: 'Stage', width: 150,
      render: (o) => {
        const track = stagesFor(o);
        return (
          <div>
            <Badge tone={forecastTone(o.stage?.forecast_category)}>{o.stage?.name}</Badge>
            {track.length > 0 && (
              <div className="stage-track mt-1">
                {track.map((s) => <span key={s.value} className={s.sortOrder <= o.stage?.sort_order ? 'on' : ''} />)}
              </div>
            )}
          </div>
        );
      },
    },
    { key: 'amount', header: 'Amount', width: 110, align: 'end', render: (o) => <span className="strong">{money(o.amount, o.currency)}</span> },
    {
      key: 'probability',
      header: 'Prob.',
      width: 70,
      align: 'end',
      render: (o) => (o.probabilityIsOverride ? (
        <span className="ovr" title={`Set manually — does not follow the stage default of ${o.stage?.probability ?? 0}%`}>{o.probability ?? 0}%*</span>
      ) : `${o.probability ?? 0}%`),
    },
    { key: 'weighted', header: 'Weighted', width: 110, align: 'end', render: (o) => money(o.weightedAmount, o.currency) },
    { key: 'closeDate', header: 'Close date', width: 110, render: (o) => <span className="t-xs">{date(o.expectedCloseDate)}</span> },
    {
      key: 'age', header: 'Age', width: 70,
      render: (o) => (o.ageDays > 45 ? <Badge tone="rose">{o.ageDays}d</Badge> : <span className="t-xs ink-3">{o.ageDays}d</span>),
    },
    {
      key: 'owner', header: 'Owner', width: 140,
      render: (o) => (o.owner
        ? <div className="d-flex align-items-center gap-2"><Avatar name={o.owner.full_name} size={22} /><span className="t-xs">{o.owner.full_name?.split(' ')[0]}</span></div>
        : <span className="text-muted">Unassigned</span>),
    },
    {
      key: 'actions',
      header: '',
      width: 60,
      align: 'end',
      render: (o) => (
        <div className="row-actions">
          <StageMenu
            opportunity={o}
            stages={allStagesFor(o)}
            canMove={can('opportunities', 'edit')}
            onPick={(target) => setStageChange({ opportunity: o, target })}
            onView={() => { setEditing(o); setFormOpen(true); }}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <p className="page-sub mb-0">
          {number(agg?.count ?? 0)} open · {moneyShort(agg?.total ?? 0)} total · {moneyShort(agg?.weighted ?? 0)} weighted
          {isMock(data) && <MockBadge section="opportunities" />}
        </p>
        <div className="d-flex flex-wrap gap-2">
          <button type="button" className="btn btn-soft btn-sm-x" onClick={() => setNotBuilt(NOT_BUILT.export)}>
            <i className="bi bi-download me-1" aria-hidden="true" />Export
          </button>
          {can('opportunities', 'create') && (
            <button type="button" className="btn btn-add btn-sm-x" onClick={() => { setEditing(null); setFormOpen(true); }}>
              <i className="bi bi-plus-lg me-1" aria-hidden="true" />New opportunity
            </button>
          )}
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-6 col-lg-3">
          <div className="stat fade-up" style={{ '--tint': '#eef2ff' }}>
            <div className="stat-label">Open value</div>
            <div className="stat-value t-xl mt-1">{moneyShort(agg?.total ?? 0)}</div>
            <div className="stat-meta mt-1">{number(agg?.count ?? 0)} opportunities</div>
          </div>
        </div>
        <div className="col-6 col-lg-3">
          <div className="stat fade-up" style={{ '--tint': '#ecfdf5' }}>
            <div className="stat-label">Won QTD</div>
            <div className="stat-value t-xl mt-1">{moneyShort(agg?.by_forecast_category?.['Closed Won'] ?? 0)}</div>
            <div className="stat-meta mt-1">This filter's forecast mix</div>
          </div>
        </div>
        <div className="col-6 col-lg-3">
          <div className="stat fade-up" style={{ '--tint': '#fff1f2' }}>
            <div className="stat-label">Lost QTD</div>
            <div className="stat-value t-xl mt-1">{moneyShort(lostData?.total_lost?.value ?? 0)}</div>
            <div className="stat-meta mt-1">
              {lostData?.by_reason?.[0] ? `Top reason: ${lostData.by_reason[0].reason.toLowerCase()} (${lostData.by_reason[0].share_pct}%)` : 'No losses yet'}
            </div>
          </div>
        </div>
        <div className="col-6 col-lg-3">
          <div className="stat fade-up" style={{ '--tint': '#fffbeb' }}>
            <div className="stat-label">Average deal</div>
            <div className="stat-value t-xl mt-1">{moneyShort(agg?.average_deal ?? 0)}</div>
            <div className="stat-meta mt-1">Across this filter</div>
          </div>
        </div>
      </div>

      <div className="card-x fade-up">
        <div className="px-3 pt-3">
          <ul className="nav nav-tabs-x">
            {TABS.map((t) => (
              <li className="nav-item" key={t.key}>
                <a className={`nav-link ${tab === t.key ? 'active' : ''}`} href={`#${t.key}`} onClick={(e) => { e.preventDefault(); onTab(t.key); }}>
                  {t.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2 p-3 border-bottom">
          <div style={{ maxWidth: 230 }}><SearchInput value={search} onChange={onFilter(setSearch)} placeholder="Search deals…" /></div>
          <ChipSelect label="Pipeline" allLabel="All pipelines" value={pipelineId} onChange={onFilter(setPipelineId)} options={options.pipelines} />
          <ChipSelect
            label="Owner" allLabel="All owners" value={ownerId} onChange={onFilter(setOwnerId)} options={options.owners}
            disabled={!canFilterByOwner} disabledReason="You can only see your own opportunities, so filtering by owner has nothing else to show."
          />
          <ChipSelect
            label="Stage" allLabel="All stages" value={stageId} onChange={onFilter(setStageId)}
            options={options.stages.filter((s) => !pipelineId || s.pipelineId === pipelineId)}
          />
        </div>

        {error ? (
          <div className="p-4"><ErrorState error={error} onRetry={refetch} /></div>
        ) : (
          <DataTable
            columns={columns}
            rows={rows}
            isLoading={isLoading || switchingTab}
            page={data?.page}
            onNext={pages.goNext}
            onPrevious={pages.goPrevious}
            canGoPrevious={pages.canGoPrevious}
            emptyTitle="No opportunities match these filters"
          />
        )}
        {!isLoading && rows.length > 0 && (
          <div className="card-foot d-flex flex-wrap align-items-center gap-3 t-xs">
            <span className="ink-3">Total <strong className="ink">{moneyShort(agg?.total ?? 0)}</strong></span>
            <span className="ink-3">Weighted <strong className="ink">{moneyShort(agg?.weighted ?? 0)}</strong></span>
            <span className="ink-3">Avg deal <strong className="ink">{moneyShort(agg?.average_deal ?? 0)}</strong></span>
          </div>
        )}
      </div>

      <div className="row g-3 mt-1">
        <div className="col-lg-6">
          <div className="card-x fade-up h-100">
            <div className="card-head"><h5>Lost opportunity analysis</h5></div>
            <div className="card-body-x">
              {!lostData ? <Spinner /> : lostData.by_reason.length === 0 ? (
                <p className="t-sm ink-3 mb-0">No lost deals recorded yet.</p>
              ) : lostData.by_reason.map((r) => (
                <div className="mb-3" key={r.reason}>
                  <div className="d-flex justify-content-between t-xs mb-1">
                    <span className="fw-600 ink-2">{r.reason}</span>
                    <span className="fw-750 tabular">{r.share_pct}%</span>
                  </div>
                  <div className="bar thick"><span style={{ width: `${r.share_pct}%`, background: 'var(--brand-500)' }} /></div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="card-x fade-up h-100">
            <div className="card-head"><h5>Competitor win/loss</h5></div>
            <div className="tbl-wrap">
              <table className="tbl">
                <thead><tr><th>Competitor</th><th className="num">Deals faced</th><th className="num">Won</th><th className="num">Lost</th><th style={{ width: 110 }}>Win rate</th></tr></thead>
                <tbody>
                  {(lostData?.by_competitor ?? []).length === 0 ? (
                    <tr><td colSpan={5} className="t-sm ink-3 text-center py-3">No competitor data recorded yet.</td></tr>
                  ) : lostData.by_competitor.map((c) => (
                    <tr key={c.competitor}>
                      <td className="strong">{c.competitor}</td>
                      <td className="num">{c.faced}</td>
                      <td className="num">{c.won}</td>
                      <td className="num">{c.lost}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div className="bar flex-grow-1"><span style={{ width: `${c.win_rate_pct}%`, background: c.win_rate_pct >= 50 ? 'var(--emerald)' : 'var(--amber)' }} /></div>
                          <span className="t-2xs fw-650">{c.win_rate_pct}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <OpportunityFormModal
        open={formOpen}
        opportunity={editing}
        options={options}
        defaultPipelineId={defaultPipelineId}
        busy={createMutation.isPending || updateMutation.isPending}
        onClose={() => setFormOpen(false)}
        onSubmit={async (payload, related) => {
          if (editing) {
            await updateMutation.mutateAsync({ id: editing.id, patch: payload, rowVersion: editing.rowVersion });
            if (related) {
              await syncOpportunityRelated(editing.id, { ...related, productOptions: options.products });
              queryClient.invalidateQueries({ queryKey: queryKeys.opportunities.all });
            }
          }
          else {
            const created = await createMutation.mutateAsync(payload);
            navigate('/opportunities');
            void created;
          }
          setFormOpen(false);
        }}
      />

      <DeleteOpportunityModal
        open={Boolean(deleting)}
        opportunity={deleting}
        busy={deleteMutation.isPending}
        onClose={() => setDeleting(null)}
        onConfirm={async (reason) => {
          await deleteMutation.mutateAsync({ id: deleting.id, reason, rowVersion: deleting.rowVersion });
          setDeleting(null);
        }}
      />

      <StageChangeModal
        open={Boolean(stageChange)}
        opportunity={stageChange?.opportunity}
        targetStage={stageChange?.target}
        options={options}
        busy={stageMutation.isPending}
        onClose={() => setStageChange(null)}
        onConfirm={async (payload) => {
          await stageMutation.mutateAsync({ id: stageChange.opportunity.id, payload: { ...payload, row_version: stageChange.opportunity.rowVersion } });
          setStageChange(null);
        }}
      />

      <NotBuiltModal open={Boolean(notBuilt)} action={notBuilt} onClose={() => setNotBuilt(null)} />
    </>
  );
}

/**
 * The row's `⋯` — `opportunities.html`'s `stage-menu` dropdown: every stage in
 * the deal's pipeline, the current one shown disabled as "current" rather than
 * omitted, plus "View deal" and "Open on board". `canMove` (an edit-permission
 * check) hides the whole stage list for a viewer with no edit rights.
 *
 * A closed deal still shows the full list — `POST /opportunities/{id}/stage`
 * answers `409 opportunity_closed` for any of them, so every entry but
 * "current" renders locked with a tooltip explaining why, rather than
 * disappearing as though the feature were unavailable.
 */
function StageMenu({ opportunity, stages, canMove, onPick, onView }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (!box.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  return (
    <div className="dropdown d-inline-block" ref={box}>
      <button
        type="button"
        className="icon-btn"
        style={{ width: 28, height: 28, fontSize: 13 }}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={`Actions for ${opportunity.name}`}
      >
        <i className="bi bi-three-dots" aria-hidden="true" />
      </button>
      <ul className={`dropdown-menu dropdown-menu-end stage-menu ${open ? 'show' : ''}`}>
        {canMove && (
          <>
            <li><h6 className="dropdown-header">Move to stage</h6></li>
            {stages.map((s) => {
              const isCurrent = s.value === opportunity.stage?.id;
              const locked = opportunity.isClosed && !isCurrent;
              if (locked) {
                return (
                  <li key={s.value}>
                    <span
                      className="dropdown-item disabled"
                      title="This opportunity is closed — closed deals can’t change stage."
                    >
                      <span className="sd" />{s.label}
                      <span className="sp">{s.probability}%</span>
                    </span>
                  </li>
                );
              }
              return (
                <li key={s.value}>
                  <a
                    className={`dropdown-item ${isCurrent ? 'is-current' : ''}`}
                    href={`#${s.value}`}
                    aria-disabled={isCurrent}
                    tabIndex={isCurrent ? -1 : 0}
                    onClick={(e) => {
                      e.preventDefault();
                      if (isCurrent) return;
                      setOpen(false);
                      onPick(s);
                    }}
                  >
                    <span className="sd" />{s.label}
                    <span className="sp">{isCurrent ? 'current' : `${s.probability}%`}</span>
                  </a>
                </li>
              );
            })}
            <li><hr className="dropdown-divider" /></li>
          </>
        )}
        <li>
          <a className="dropdown-item" href={`#${opportunity.id}`} onClick={(e) => { e.preventDefault(); setOpen(false); onView(); }}>
            <i className="bi bi-eye me-2" aria-hidden="true" />View deal
          </a>
        </li>
        <li>
          <Link className="dropdown-item" to="/opportunities?view=board" onClick={() => setOpen(false)}>
            <i className="bi bi-kanban me-2" aria-hidden="true" />Open on board
          </Link>
        </li>
      </ul>
    </div>
  );
}
